import { interval } from 'rxjs'
import { action, makeAutoObservable, observable, reaction } from 'mobx'
import { Router } from '../../types/definitions'
import { EmailConfirmationAPI } from '../API'
import { takeWhile } from 'rxjs/operators'
import { jwtDecode } from 'jwt-decode'
import { AlertFactory, AlertState } from '../../alerts/Alert'

/**
 * Payload for the session token.
 */
type SessionTokenPayload = {
  iat: number
  exp: number
  email: string
}

/**
 * Confirm Email View Model.
 */
export class ConfirmEmailViewModel {
  /**
   * The user's email, based on the token.
   */
  @observable
  email = ''

  /**
   * Indicates whether the user's email has been confirmed.
   */
  @observable
  emailConfirmed = false

  /**
   * Indicates whether the confirmation attempt has expired based on the token.
   */
  @observable
  pageExpired = false

  /**
   * Email Confirmation API.
   */
  private api?: EmailConfirmationAPI

  /**
   * 5 second ticker.
   */
  private readonly _timer = interval(5000).pipe(
    takeWhile(() => !this.emailConfirmed),
  )

  constructor(
    private readonly router: Router,
    private readonly newAlert: AlertFactory,
  ) {
    makeAutoObservable(this)

    reaction(
      () => this.emailConfirmed,
      (confirmed) => {
        if (confirmed) {
          this.router.push('/email-confirmed')
        }
      },
    )
  }

  /**
   * Resends the verification email.
   */
  resendVerificationEmail = async () => {
    await this.api
      ?.resendEmail()
      .catch(() =>
        this.newAlert()
          .saying('An error occurred')
          .withState(AlertState.Error)
          .show(),
      )
  }

  /**
   * Activates the view model.
   */
  @action.bound
  activate(token: string) {
    this.api = new EmailConfirmationAPI(token)

    const { email, exp } = jwtDecode<SessionTokenPayload>(token)
    if (!email || !exp) {
      this.newAlert().saying('Invalid token').withState(AlertState.Error).show()
      return
    }

    this.email = email

    const expiresAt = exp * 1000
    this._timer.subscribe(
      action(() => {
        this.checkTokenExpiration(expiresAt)
        this.checkVerificationStatus()
      }),
    )

    // Check token expiration immediately upon activation as well.
    this.checkTokenExpiration(expiresAt)
  }

  /**
   * Checks the email verification status of the user. Used to automatically update the page.
   */
  private checkVerificationStatus = () => {
    this.api?.getVerificationStatus().then(
      action((status) => {
        this.emailConfirmed = status
      }),
    )
  }

  /**
   * Checks whether the token has expired.
   */
  @action.bound
  private checkTokenExpiration(expiresAt: number) {
    if (Date.now() > expiresAt) {
      this.pageExpired = true
    }
  }
}
